import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { NutritionAiPanel, totalsFromMealItems, type MealEstimate } from '../components/NutritionAiPanel';
import { FoodSearchPanel } from '../components/nutrition/FoodSearchPanel';
import { AffiliatePlacement } from '../components/AffiliatePlacement';
import {
  BottomSheet,
  CollapsiblePanel,
  MealSectionCard,
  MetricTile,
  PremiumCard,
  PremiumPage,
  PrimaryButton,
  StatRing,
} from '../components/premium';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { hasAiCoachingAccess } from '../lib/coachAccess';
import { localDateKey, localDateKeyFromIso, noonIsoForDateKey } from '../lib/dateKey';
import { datetimeLocalToIso, isoToDatetimeLocal } from '../lib/datetimeLocal';
import { isMealSlot, localTimeForSlot, MEAL_SLOT_LABEL, slotFromHour, type MealSlot } from '../lib/mealSlots';
import { calorieTargetForDateKey } from '../lib/weeklyCalories';
import {
  flOzToMl,
  formatWaterWithAlt,
  getWaterUnitPref,
  mlToFlOz,
  roundMl,
  setWaterUnitPref,
  type WaterUnitPref,
} from '../lib/waterUnits';
import type { Meal, UserProfile } from '../types';
import { fieldControl } from '../lib/uiClasses';

type WaterEntryRow = { id: string; ml: number; loggedAt: string };

type DayData = {
  totals: {
    calories: number;
    proteinG: number;
    carbsG: number;
    fatsG: number;
    fiberG: number;
    sugarG: number;
    sodiumMg: number;
  };
  waterMl: number;
  meals: Meal[];
  waterEntries?: WaterEntryRow[];
};

type MealEditState = {
  id: string;
  name: string;
  cal: string;
  p: string;
  c: string;
  f: string;
  fiber: string;
  sugar: string;
  sodium: string;
  loggedAtLocal: string;
  mealSlot: MealSlot;
};

function optNutrientNumber(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function dateKeyFromSearch(sp: URLSearchParams): string {
  const q = sp.get('date');
  if (q && /^\d{4}-\d{2}-\d{2}$/.test(q)) return q;
  return localDateKey();
}

export function NutritionPage() {
  const { user } = useAuth();
  const location = useLocation();
  const quickAddAnchorRef = useRef<HTMLDivElement>(null);
  const waterLogRef = useRef<HTMLDivElement>(null);
  const coachUnlocked = hasAiCoachingAccess(user);
  const [searchParams] = useSearchParams();
  const [dateKey, setDateKey] = useState(() => dateKeyFromSearch(searchParams));
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [data, setData] = useState<DayData | null>(null);
  const [name, setName] = useState('');
  const [cal, setCal] = useState('500');
  const [p, setP] = useState('40');
  const [c, setC] = useState('40');
  const [f, setF] = useState('10');
  const [fiber, setFiber] = useState('');
  const [sugar, setSugar] = useState('');
  const [sodium, setSodium] = useState('');
  const [mealLoggedAtLocal, setMealLoggedAtLocal] = useState(() => `${dateKeyFromSearch(searchParams)}T08:00`);
  const [mealSlot, setMealSlot] = useState<MealSlot>('breakfast');

  const [waterUnit, setWaterUnit] = useState<WaterUnitPref>(() => getWaterUnitPref());
  const [customWaterAmount, setCustomWaterAmount] = useState(() =>
    getWaterUnitPref() === 'fl_oz' ? '8.5' : '250',
  );
  const [cupLoggedAtLocal, setCupLoggedAtLocal] = useState(() => `${dateKeyFromSearch(searchParams)}T12:00`);
  const [waterBusy, setWaterBusy] = useState(false);
  const [waterNote, setWaterNote] = useState<string | null>(null);
  const [mealNote, setMealNote] = useState<string | null>(null);
  const [mealEdit, setMealEdit] = useState<MealEditState | null>(null);
  const [mealEditBusy, setMealEditBusy] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [waterSheetOpen, setWaterSheetOpen] = useState(false);

  const mealsBySlot = useMemo(() => {
    const slots = { breakfast: [] as Meal[], lunch: [] as Meal[], dinner: [] as Meal[], snacks: [] as Meal[] };
    if (!data?.meals) return slots;
    for (const m of data.meals) {
      const slot = isMealSlot(m.mealSlot) ? m.mealSlot : slotFromHour(new Date(m.loggedAt).getHours());
      slots[slot].push(m);
    }
    return slots;
  }, [data?.meals]);

  function openQuickAdd(slot: MealSlot = 'breakfast') {
    setMealSlot(slot);
    setMealLoggedAtLocal(localTimeForSlot(dateKey, slot));
    setQuickAddOpen(true);
  }

  useEffect(() => {
    const q = searchParams.get('date');
    if (q && /^\d{4}-\d{2}-\d{2}$/.test(q)) {
      setDateKey((prev) => (prev === q ? prev : q));
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('focus') !== 'water') return;
    setWaterSheetOpen(true);
    const t = window.setTimeout(() => {
      waterLogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [searchParams, location.pathname]);

  async function refreshDay() {
    setData(await api<DayData>(`/api/nutrition/day/${dateKey}`));
  }

  useEffect(() => {
    (async () => {
      try {
        const [prof, d] = await Promise.all([
          api<UserProfile | null>('/api/profile'),
          api<DayData>(`/api/nutrition/day/${dateKey}`),
        ]);
        setProfile(prof);
        setData(d);
      } catch {
        setData(null);
      }
    })();
  }, [dateKey, location.pathname]);

  useEffect(() => {
    setMealLoggedAtLocal(localTimeForSlot(dateKey, mealSlot));
    setCupLoggedAtLocal(`${dateKey}T12:00`);
  }, [dateKey, mealSlot]);

  const calorieTarget = calorieTargetForDateKey(profile, dateKey);
  const totals = data?.totals;

  function applyEstimate(est: MealEstimate) {
    const totals = est.items.length ? totalsFromMealItems(est.items) : est;
    setName(est.suggestedName);
    setCal(String(totals.calories));
    setP(String(totals.proteinG));
    setC(String(totals.carbsG));
    setF(String(totals.fatsG));
    if (totals.fiberG != null) setFiber(String(totals.fiberG));
    if (totals.sugarG != null) setSugar(String(totals.sugarG));
    if (totals.sodiumMg != null) setSodium(String(totals.sodiumMg));
    window.setTimeout(() => {
      quickAddAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      document.getElementById('meal-name')?.focus({ preventScroll: true });
    }, 50);
  }

  async function addMeal() {
    if (!name.trim()) return;
    setMealNote(null);
    const fromPicker = datetimeLocalToIso(mealLoggedAtLocal);
    const loggedAt = fromPicker ?? noonIsoForDateKey(dateKey);
    if (fromPicker && localDateKeyFromIso(fromPicker) !== dateKey) {
      setMealNote('Meal time must fall on the selected date (adjust the date picker or the time).');
      return;
    }
    const body: Record<string, unknown> = {
      name: name.trim(),
      calories: Number(cal) || 0,
      proteinG: Number(p) || 0,
      carbsG: Number(c) || 0,
      fatsG: Number(f) || 0,
      loggedAt,
      mealSlot,
    };
    const fiberN = optNutrientNumber(fiber);
    const sugarN = optNutrientNumber(sugar);
    const sodiumN = optNutrientNumber(sodium);
    if (fiberN != null) body.fiberG = fiberN;
    if (sugarN != null) body.sugarG = sugarN;
    if (sodiumN != null) body.sodiumMg = sodiumN;
    await api('/api/nutrition/meals', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    setName('');
    setFiber('');
    setSugar('');
    setSodium('');
    await refreshDay();
  }

  async function logEstimate(est: MealEstimate) {
    const totals = est.items.length ? totalsFromMealItems(est.items) : est;
    if (!est.suggestedName.trim() || !(totals.calories > 0)) return;
    setMealNote(null);
    const fromPicker = datetimeLocalToIso(mealLoggedAtLocal);
    const loggedAt = fromPicker ?? noonIsoForDateKey(dateKey);
    if (fromPicker && localDateKeyFromIso(fromPicker) !== dateKey) {
      setMealNote('Meal time must fall on the selected date (adjust the date picker or the time).');
      return;
    }
    const body: Record<string, unknown> = {
      name: est.suggestedName.trim(),
      calories: totals.calories,
      proteinG: totals.proteinG,
      carbsG: totals.carbsG,
      fatsG: totals.fatsG,
      loggedAt,
      mealSlot,
    };
    if (totals.fiberG != null) body.fiberG = totals.fiberG;
    if (totals.sugarG != null) body.sugarG = totals.sugarG;
    if (totals.sodiumMg != null) body.sodiumMg = totals.sodiumMg;
    await api('/api/nutrition/meals', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    await refreshDay();
    setQuickAddOpen(false);
  }

  function startMealEdit(m: Meal) {
    setMealNote(null);
    setMealEdit({
      id: m.id,
      name: m.name,
      cal: String(m.calories),
      p: String(m.proteinG),
      c: String(m.carbsG),
      f: String(m.fatsG),
      fiber: m.fiberG != null ? String(m.fiberG) : '',
      sugar: m.sugarG != null ? String(m.sugarG) : '',
      sodium: m.sodiumMg != null ? String(m.sodiumMg) : '',
      loggedAtLocal: isoToDatetimeLocal(m.loggedAt),
      mealSlot: isMealSlot(m.mealSlot) ? m.mealSlot : slotFromHour(new Date(m.loggedAt).getHours()),
    });
  }

  function cancelMealEdit() {
    setMealEdit(null);
    setMealNote(null);
  }

  async function saveMealEdit() {
    if (!mealEdit) return;
    setMealNote(null);
    const fromPicker = datetimeLocalToIso(mealEdit.loggedAtLocal);
    const loggedAt = fromPicker ?? noonIsoForDateKey(dateKey);
    if (fromPicker && localDateKeyFromIso(fromPicker) !== dateKey) {
      setMealNote('Meal time must fall on the selected date.');
      return;
    }
    const body: Record<string, unknown> = {
      name: mealEdit.name.trim(),
      calories: Number(mealEdit.cal) || 0,
      proteinG: Number(mealEdit.p) || 0,
      carbsG: Number(mealEdit.c) || 0,
      fatsG: Number(mealEdit.f) || 0,
      loggedAt,
      mealSlot: mealEdit.mealSlot,
    };
    body.fiberG = optNutrientNumber(mealEdit.fiber);
    body.sugarG = optNutrientNumber(mealEdit.sugar);
    body.sodiumMg = optNutrientNumber(mealEdit.sodium);
    setMealEditBusy(true);
    try {
      await api(`/api/nutrition/meals/${mealEdit.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setMealEdit(null);
      await refreshDay();
    } catch {
      setMealNote('Could not update meal.');
    } finally {
      setMealEditBusy(false);
    }
  }

  async function removeMeal(id: string) {
    setMealNote(null);
    setMealEditBusy(true);
    try {
      await api(`/api/nutrition/meals/${id}`, { method: 'DELETE' });
      setMealEdit(null);
      await refreshDay();
    } catch {
      setMealNote('Could not delete meal.');
    } finally {
      setMealEditBusy(false);
    }
  }

  async function addWaterAmount(ml: number) {
    setWaterBusy(true);
    setWaterNote(null);
    try {
      const fromPicker = datetimeLocalToIso(cupLoggedAtLocal);
      if (fromPicker && localDateKeyFromIso(fromPicker) !== dateKey) {
        setWaterNote('Drink time must fall on the selected calendar date (change the date above or the time).');
        return;
      }
      await api(`/api/nutrition/day/${dateKey}/water-entry`, {
        method: 'POST',
        body: JSON.stringify({
          ml,
          ...(fromPicker ? { loggedAt: fromPicker } : {}),
        }),
      });
      await refreshDay();
      setWaterNote('Logged.');
      setCupLoggedAtLocal(`${dateKey}T12:00`);
    } catch {
      setWaterNote('Could not log water.');
    } finally {
      setWaterBusy(false);
    }
  }

  function changeWaterUnit(next: WaterUnitPref) {
    const n = Number(customWaterAmount);
    if (Number.isFinite(n) && n > 0) {
      if (next === 'fl_oz' && waterUnit === 'ml') {
        setCustomWaterAmount(mlToFlOz(n).toFixed(1).replace(/\.0$/, ''));
      } else if (next === 'ml' && waterUnit === 'fl_oz') {
        setCustomWaterAmount(String(roundMl(flOzToMl(n))));
      }
    } else if (next === 'fl_oz') {
      setCustomWaterAmount('8');
    } else {
      setCustomWaterAmount('250');
    }
    setWaterUnit(next);
    setWaterUnitPref(next);
  }

  function customAmountToMl(): number {
    const n = Number(customWaterAmount);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return waterUnit === 'fl_oz' ? roundMl(flOzToMl(n)) : roundMl(n);
  }

  async function removeWaterEntry(id: string) {
    setWaterBusy(true);
    setWaterNote(null);
    try {
      await api(`/api/nutrition/water-entry/${id}`, { method: 'DELETE' });
      await refreshDay();
    } catch {
      setWaterNote('Could not remove entry.');
    } finally {
      setWaterBusy(false);
    }
  }

  const proteinTargetG = profile?.proteinTargetG ?? 150;
  const carbsTarget = Math.round((calorieTarget * 0.4) / 4);
  const fatTarget = Math.round((calorieTarget * 0.3) / 9);

  function renderMealRow(m: Meal) {
    if (mealEdit?.id === m.id) {
      return (
        <li key={m.id} className="rounded-xl border border-sky-500/25 bg-sky-500/10 px-3 py-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-[11px] text-zinc-500 sm:col-span-2">
              Name
              <input
                value={mealEdit.name}
                onChange={(e) => setMealEdit((s) => (s ? { ...s, name: e.target.value } : s))}
                className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white"
              />
            </label>
            <label className="text-[11px] text-zinc-500">
              Calories
              <input
                inputMode="numeric"
                value={mealEdit.cal}
                onChange={(e) => setMealEdit((s) => (s ? { ...s, cal: e.target.value } : s))}
                className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white"
              />
            </label>
            <label className="text-[11px] text-zinc-500">
              Protein
              <input inputMode="decimal" value={mealEdit.p} onChange={(e) => setMealEdit((s) => (s ? { ...s, p: e.target.value } : s))} className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white" />
            </label>
            <label className="text-[11px] text-zinc-500">
              Carbs
              <input inputMode="decimal" value={mealEdit.c} onChange={(e) => setMealEdit((s) => (s ? { ...s, c: e.target.value } : s))} className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white" />
            </label>
            <label className="text-[11px] text-zinc-500">
              Fat
              <input inputMode="decimal" value={mealEdit.f} onChange={(e) => setMealEdit((s) => (s ? { ...s, f: e.target.value } : s))} className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white" />
            </label>
            <label className="text-[11px] text-zinc-500">
              Fiber (g)
              <input inputMode="decimal" value={mealEdit.fiber} onChange={(e) => setMealEdit((s) => (s ? { ...s, fiber: e.target.value } : s))} className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white" />
            </label>
            <label className="text-[11px] text-zinc-500">
              Sugar (g)
              <input inputMode="decimal" value={mealEdit.sugar} onChange={(e) => setMealEdit((s) => (s ? { ...s, sugar: e.target.value } : s))} className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white" />
            </label>
            <label className="text-[11px] text-zinc-500">
              Sodium (mg)
              <input inputMode="decimal" value={mealEdit.sodium} onChange={(e) => setMealEdit((s) => (s ? { ...s, sodium: e.target.value } : s))} className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white" />
            </label>
            <label className="text-[11px] text-zinc-500 sm:col-span-2">
              Meal
              <select
                value={mealEdit.mealSlot}
                onChange={(e) => {
                  const slot = e.target.value as MealSlot;
                  setMealEdit((s) =>
                    s ? { ...s, mealSlot: slot, loggedAtLocal: localTimeForSlot(dateKey, slot) } : s,
                  );
                }}
                className="mt-1 w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-sm text-white"
              >
                {(Object.keys(MEAL_SLOT_LABEL) as MealSlot[]).map((slot) => (
                  <option key={slot} value={slot}>
                    {MEAL_SLOT_LABEL[slot]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" disabled={mealEditBusy || !mealEdit.name.trim()} onClick={() => void saveMealEdit()} className="rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {mealEditBusy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" disabled={mealEditBusy} onClick={cancelMealEdit} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-zinc-300">
              Cancel
            </button>
            <button type="button" disabled={mealEditBusy} onClick={() => void removeMeal(mealEdit.id)} className="rounded-lg border border-red-500/30 px-3 py-1.5 text-xs font-semibold text-red-300">
              Delete
            </button>
          </div>
        </li>
      );
    }
    return (
      <li key={m.id} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-zinc-100">{m.name}</div>
          <div className="text-[11px] tabular-nums text-zinc-500">
            {m.calories} kcal · P{m.proteinG} C{m.carbsG} F{m.fatsG}
            {m.fiberG != null ? ` · fiber ${m.fiberG}g` : ''}
            {m.sugarG != null ? ` · sugar ${m.sugarG}g` : ''}
            {m.sodiumMg != null ? ` · Na ${m.sodiumMg}mg` : ''}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={() => startMealEdit(m)} disabled={mealEdit != null} className="text-[11px] font-medium text-accent disabled:opacity-40">
            Edit
          </button>
          <button type="button" onClick={() => void removeMeal(m.id)} disabled={mealEdit != null || mealEditBusy} className="text-[11px] font-medium text-red-300/90 disabled:opacity-40">
            Delete
          </button>
        </div>
      </li>
    );
  }

  return (
    <PremiumPage
      title="Nutrition"
      right={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openQuickAdd()}
            className="rounded-xl bg-accent px-3 py-2 text-xs font-bold text-white shadow-sm"
          >
            + Food
          </button>
          <input type="date" value={dateKey} onChange={(e) => setDateKey(e.target.value)} className={`${fieldControl} !w-auto !min-h-[44px] !py-2 text-xs`} />
        </div>
      }
    >

      <PremiumCard variant="nutrition">
        <div className="flex flex-wrap items-center justify-center gap-5 sm:justify-between">
          <StatRing
            value={totals?.calories ?? 0}
            max={calorieTarget}
            label="Calories"
            display={String(totals?.calories ?? 0)}
            unit={`/ ${calorieTarget}`}
            size={112}
          />
          <div className="text-center sm:text-right">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400/80">Remaining</div>
            <div className="font-display text-2xl font-bold tabular-nums text-[var(--text-primary)]">
              {Math.max(0, calorieTarget - (totals?.calories ?? 0))}
            </div>
            <div className="text-xs text-[var(--text-muted)]">kcal left</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <MetricTile
            label="Protein"
            value={`${Math.round(totals?.proteinG ?? 0)} / ${proteinTargetG}g`}
            sub={`${Math.max(0, proteinTargetG - Math.round(totals?.proteinG ?? 0))}g left`}
            tone="success"
          />
          <MetricTile
            label="Carbs"
            value={`${Math.round(totals?.carbsG ?? 0)} / ${carbsTarget}g`}
            sub={`${Math.max(0, carbsTarget - Math.round(totals?.carbsG ?? 0))}g left`}
          />
          <MetricTile
            label="Fat"
            value={`${Math.round(totals?.fatsG ?? 0)} / ${fatTarget}g`}
            sub={`${Math.max(0, fatTarget - Math.round(totals?.fatsG ?? 0))}g left`}
          />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <MetricTile label="Fiber" value={`${Math.round(totals?.fiberG ?? 0)}g`} sub="optional extra" />
          <MetricTile label="Sugar" value={`${Math.round(totals?.sugarG ?? 0)}g`} sub="optional extra" />
          <MetricTile label="Sodium" value={`${Math.round(totals?.sodiumMg ?? 0)}mg`} sub="optional extra" />
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
          Calories, protein, carbs, and fat are the main log. Fiber, sugar, and sodium are optional extras on every meal — fill them in when you have a label.
        </p>
      </PremiumCard>

      {coachUnlocked ? (
        <PremiumCard variant="ai" className="!p-3">
          <button type="button" onClick={() => setQuickAddOpen(true)} className="flex w-full items-center justify-between text-sm font-semibold text-violet-200">
            <span className="flex items-center gap-2">
              AI meal estimate
              <span className="text-[10px] text-violet-300/70" title="Estimates vary — verify labels">ⓘ</span>
            </span>
            <span className="text-xs">Open →</span>
          </button>
        </PremiumCard>
      ) : null}

      <AffiliatePlacement slot="inline" />

      <div id="water-log" ref={waterLogRef} className="scroll-mt-24">
      <CollapsiblePanel title="Water" summary={data?.waterMl != null ? formatWaterWithAlt(data.waterMl, waterUnit) : 'Log water'} open={waterSheetOpen} onOpenChange={setWaterSheetOpen}>
        <PremiumCard className="!p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-sm font-bold text-[var(--text-primary)]">Water</h2>
          <span className="text-sm font-bold tabular-nums text-cyan-300">
            {data?.waterMl != null ? formatWaterWithAlt(data.waterMl, waterUnit) : '—'}
          </span>
        </div>
        <div className="mb-3 flex rounded-xl border border-white/10 p-0.5">
          {(['ml', 'fl_oz'] as const).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => changeWaterUnit(u)}
              className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                waterUnit === u ? 'bg-white/10 text-white' : 'text-zinc-500'
              }`}
            >
              {u === 'ml' ? 'ml' : 'fl oz'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {(waterUnit === 'fl_oz' ? [8, 12, 16, 24] : [250, 355, 500, 750]).map((n) => (
            <button
              key={`${waterUnit}-${n}`}
              type="button"
              disabled={waterBusy}
              onClick={() => void addWaterAmount(waterUnit === 'fl_oz' ? flOzToMl(n) : n)}
              className="water-preset-chip disabled:opacity-50"
            >
              +{n} {waterUnit === 'fl_oz' ? 'oz' : 'ml'}
            </button>
          ))}
          <button
            type="button"
            disabled={waterBusy}
            onClick={() => document.getElementById('custom-water-input')?.focus()}
            className="water-preset-chip disabled:opacity-50"
          >
            Custom
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="block space-y-1">
            <span className="text-[11px] text-zinc-500">Custom amount ({waterUnit === 'fl_oz' ? 'fl oz' : 'ml'})</span>
            <input id="custom-water-input" inputMode="decimal" value={customWaterAmount} onChange={(e) => setCustomWaterAmount(e.target.value)} className={`${fieldControl} !w-24`} />
          </label>
          <PrimaryButton className="!min-h-[44px] !py-2 !text-xs" disabled={waterBusy || customAmountToMl() <= 0} onClick={() => void addWaterAmount(customAmountToMl())}>
            Add
          </PrimaryButton>
        </div>
        {data?.waterEntries && data.waterEntries.length > 0 ? (
          <ul className="mt-3 space-y-1.5">
            {data.waterEntries.map((w) => (
              <li key={w.id} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.03] px-2.5 py-1.5 text-xs text-zinc-300">
                <span>{formatWaterWithAlt(w.ml, waterUnit)}</span>
                <button type="button" disabled={waterBusy} onClick={() => void removeWaterEntry(w.id)} className="text-[11px] font-medium text-red-300/90 disabled:opacity-40">
                  Delete
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {waterNote ? <p className="mt-2 text-xs text-[var(--text-muted)]">{waterNote}</p> : null}
        </PremiumCard>
      </CollapsiblePanel>
      </div>

      <div className="space-y-3">
          {(
            [
              ['breakfast', mealsBySlot.breakfast],
              ['lunch', mealsBySlot.lunch],
              ['dinner', mealsBySlot.dinner],
              ['snacks', mealsBySlot.snacks],
            ] as const
          ).map(([slot, meals]) => (
            <MealSectionCard key={slot} title={MEAL_SLOT_LABEL[slot]} meals={meals.length} onAdd={() => openQuickAdd(slot)}>
              {meals.length > 0 ? <ul className="space-y-2">{meals.map(renderMealRow)}</ul> : (
                <p className="text-[11px] text-zinc-600">Nothing logged yet</p>
              )}
            </MealSectionCard>
          ))}
        </div>

      <BottomSheet open={quickAddOpen} onClose={() => setQuickAddOpen(false)} title={`Add ${MEAL_SLOT_LABEL[mealSlot].toLowerCase()}`}>
        <div ref={quickAddAnchorRef} className="grid gap-3">
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(MEAL_SLOT_LABEL) as MealSlot[]).map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => {
                  setMealSlot(slot);
                  setMealLoggedAtLocal(localTimeForSlot(dateKey, slot));
                }}
                className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${
                  mealSlot === slot ? 'bg-accent text-white' : 'border border-white/10 text-zinc-400'
                }`}
              >
                {MEAL_SLOT_LABEL[slot]}
              </button>
            ))}
          </div>
          <FoodSearchPanel mealSlot={mealSlot} loggedAtLocal={mealLoggedAtLocal} onLogged={() => { void refreshDay(); setQuickAddOpen(false); }} />
          <div className="border-t border-white/8 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Or enter it yourself</p>
          <label className="mt-2 block space-y-1">
            <span className="text-xs font-medium text-zinc-400">Meal name</span>
            <input id="meal-name" placeholder="e.g. Chicken burrito bowl" value={name} onChange={(e) => setName(e.target.value)} className={fieldControl} />
          </label>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Calories</span>
              <input inputMode="numeric" placeholder="kcal" value={cal} onChange={(e) => setCal(e.target.value)} className={fieldControl} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Protein</span>
              <input inputMode="decimal" placeholder="g" value={p} onChange={(e) => setP(e.target.value)} className={fieldControl} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Carbs</span>
              <input inputMode="decimal" placeholder="g" value={c} onChange={(e) => setC(e.target.value)} className={fieldControl} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Fat</span>
              <input inputMode="decimal" placeholder="g" value={f} onChange={(e) => setF(e.target.value)} className={fieldControl} />
            </label>
          </div>
          <p className="mt-3 text-[11px] text-zinc-500">Optional extras — fiber, sugar, and sodium when you have a label.</p>
          <div className="grid grid-cols-3 gap-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Fiber (g)</span>
              <input inputMode="decimal" placeholder="—" value={fiber} onChange={(e) => setFiber(e.target.value)} className={fieldControl} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Sugar (g)</span>
              <input inputMode="decimal" placeholder="—" value={sugar} onChange={(e) => setSugar(e.target.value)} className={fieldControl} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-zinc-400">Sodium (mg)</span>
              <input inputMode="decimal" placeholder="—" value={sodium} onChange={(e) => setSodium(e.target.value)} className={fieldControl} />
            </label>
          </div>
          <PrimaryButton className="mt-3" fullWidth onClick={() => addMeal().then(() => setQuickAddOpen(false)).catch(() => {})}>
            Add meal
          </PrimaryButton>
          {mealNote ? <p className="text-sm text-amber-300">{mealNote}</p> : null}
          {coachUnlocked ? (
            <div id="nutrition-ai" className="mt-2 border-t border-white/8 pt-4">
              <NutritionAiPanel
                mealSlotLabel={MEAL_SLOT_LABEL[mealSlot]}
                onApplyToQuickAdd={(est) => { applyEstimate(est); }}
                onLogEstimate={(est) => logEstimate(est)}
              />
            </div>
          ) : null}
          </div>
        </div>
      </BottomSheet>
    </PremiumPage>
  );
}
